// src/pages/ConfirmEmailPage.js
import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import useAuth from "../hooks/useAuth";

const ConfirmEmailPage = () => {
  const [searchParams] = useSearchParams();
  const [message, setMessage] = useState("Confirming your email...");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const { confirmEmail } = useAuth();
  const navigate = useNavigate();

  const messageRef = useRef(null);

  useEffect(() => {
    const processConfirmation = async () => {
      const userId = searchParams.get("userId");
      const code = searchParams.get("code");

      if (!userId || !code) {
        setError("Invalid confirmation link. Missing parameters.");
        setIsLoading(false);
        return;
      }

      try {
        const response = await confirmEmail(userId, code);
        setMessage(response.message || "Email confirmed successfully!");
        setTimeout(() => navigate("/login"), 3000);
      } catch (err) {
        setError(
          err.details?.message ||
            err.message ||
            "Failed to confirm email. The link may be invalid or expired."
        );
      } finally {
        setIsLoading(false);
      }
    };

    processConfirmation();
  }, [searchParams, confirmEmail, navigate]);

  useEffect(() => {
    if (!isLoading && messageRef.current) {
      messageRef.current.focus();
    }
  }, [isLoading, error, message]);


  return (
    <main className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center px-4 sm:px-6 lg:px-8">
      <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-r from-blue-600/10 to-purple-600/10"></div>

      <div className="relative max-w-md w-full">
        <div className="bg-slate-800/80 backdrop-blur-sm rounded-2xl p-8 border border-slate-700/50 shadow-2xl">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center text-white mx-auto mb-4">
              {isLoading ? (
                <svg
                  aria-hidden="true"
                  className="animate-spin w-8 h-8"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
              ) : error ? (
                <svg
                  aria-hidden="true"
                  className="w-8 h-8"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              ) : (
                <svg
                  aria-hidden="true"
                  className="w-8 h-8"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              )}
            </div>
            <h2 className="text-3xl font-bold text-slate-100 mb-2"> {/* Contrast Fix */}
              Email Confirmation
            </h2>
            <p className="text-slate-300">Verifying your account</p> {/* Contrast Fix */}
          </div>

          <div className="text-center">
            {isLoading && (
              <div role="status" aria-live="polite" ref={messageRef} tabIndex={-1} className="bg-blue-700/20 border border-blue-600/30 rounded-xl p-6 outline-none"> {/* Contrast Fix for background, border, focus */}
                <div className="w-12 h-12 bg-blue-600/30 rounded-full flex items-center justify-center mx-auto mb-4"> {/* Contrast Fix */}
                  <svg
                    aria-hidden="true"
                    className="animate-spin w-6 h-6 text-sky-300" /* Contrast Fix */
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                </div>
                <p className="text-sky-200 text-lg font-medium">{message}</p> {/* Contrast Fix */}
              </div>
            )}

            {!isLoading && error && (
              <div role="alert" aria-live="assertive" ref={messageRef} tabIndex={-1} className="bg-red-700/20 border border-red-600/30 rounded-xl p-6 outline-none"> {/* Contrast Fix for background, border, focus */}
                <div className="w-12 h-12 bg-red-600/30 rounded-full flex items-center justify-center mx-auto mb-4"> {/* Contrast Fix */}
                  <svg
                    aria-hidden="true"
                    className="w-6 h-6 text-red-300" /* Contrast Fix */
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <p className="text-red-200 mb-6">{error}</p> {/* Contrast Fix */}
                <Link
                  to="/register"
                  className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-semibold rounded-xl hover:shadow-lg transform hover:scale-105 transition-all duration-300"
                >
                  Try Again
                  <svg
                    aria-hidden="true"
                    className="w-4 h-4 ml-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                </Link>
              </div>
            )}

            {!isLoading && !error && (
              <div role="status" aria-live="polite" ref={messageRef} tabIndex={-1} className="bg-green-700/20 border border-green-600/30 rounded-xl p-6 outline-none"> {/* Contrast Fix for background, border, focus */}
                <div className="w-12 h-12 bg-green-600/30 rounded-full flex items-center justify-center mx-auto mb-4"> {/* Contrast Fix */}
                  <svg
                    aria-hidden="true"
                    className="w-6 h-6 text-green-300" /* Contrast Fix */
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
                <p className="text-green-200 mb-2 text-lg font-medium"> {/* Contrast Fix */}
                  {message}
                </p>
                <p className="text-slate-400 text-sm mb-6"> {/* Contrast Fix */}
                  Redirecting you to login...
                </p>
                <Link
                  to="/login"
                  className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-semibold rounded-xl hover:shadow-lg transform hover:scale-105 transition-all duration-300"
                >
                  Continue to Login
                  <svg
                    aria-hidden="true"
                    className="w-4 h-4 ml-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 7l5 5m0 0l-5 5m5-5H6"
                    />
                  </svg>
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </main>
  );
};

export default ConfirmEmailPage;
